import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';
import Pagination from './TablePagination';
import ImageWithPreview from './ImageWithPreview';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { API_URL } from '../config';
import { usePermissions, useCurrentPageKey } from '../PermissionsContext';

const SORT_ORDER = {
  NONE: 'NONE',
  ASC: 'ASC',
  DESC: 'DESC'
};

const STATUS_OPTIONS = ['In production', 'Complete'];

const DataTable = ({ title, columns, endpoint, searchTerm, filterParams, onEdit, updateKey, keyFieldsConfig, showStatus, onAddFromPlanned }) => {
  const [data, setDataState] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(1000);
  const [totalItems, setTotalItems] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, order: SORT_ORDER.NONE });
  const [activeStatusRow, setActiveStatusRow] = useState(null);
  const { can } = usePermissions();
  const canDelete = can(useCurrentPageKey(), 'delete');
  const fetchIdRef = useRef(0);

  const formatDateTime = (date, time = '', type = 'date') => {
    if (!date && !time) return '';
    if (type === 'date' && date && typeof date === 'string') {
      const parts = date.split('T')[0].split('-');
      if (parts.length === 3) return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }
    let parsedDate = time ? new Date() : new Date(date);
    if (time) {
      const [hours, minutes, seconds] = time.split(':').map(Number);
      parsedDate.setUTCHours(hours, minutes, seconds || 0);
    }
    if (isNaN(parsedDate.getTime())) return '';
    const options = { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' };
    if (type === 'datetime') {
      return new Intl.DateTimeFormat('en-US', { ...options, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).format(parsedDate);
    }
    if (type === 'time') {
      return new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'America/New_York' }).format(parsedDate);
    }
    return new Intl.DateTimeFormat('en-US', options).format(parsedDate);
  };

  const formatTime = (timeStr) => {
    if (!timeStr || timeStr === '00:00:00') return '';
    const parts = timeStr.split(':');
    if (parts.length < 2) return '';
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    date.setSeconds(0);
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  function calculateHoursTook(row) {
    const { startTime, endTime, starttimes, endtimes } = row;

    function timeToMinutes(t) {
      if (!t || t === '00:00:00') return null;
      const parts = t.split(':').map(Number);
      const h = parts[0] || 0;
      const m = parts[1] || 0;
      const s = parts[2] || 0;
      return h * 60 + m + s / 60;
    }

    function minutesToHours(m) {
      return m > 0 ? Math.round((m / 60) * 100) / 100 : 0;
    }

    const shiftStart = timeToMinutes('08:00:00'); // 480
    const shiftEnd = timeToMinutes('23:00:00');   // 1380

    function calculateSegment(start, end) {
      if (!start || !end || end === '00:00:00') return 0;

      const startMin = timeToMinutes(start);
      const endMin = timeToMinutes(end);

      if (startMin === null || endMin === null) return 0;

      const clampedStart = Math.max(startMin, shiftStart);
      const clampedEnd = Math.min(endMin, shiftEnd);

      let minutes;
      if (endMin >= startMin) {
        minutes = Math.max(0, clampedEnd - clampedStart);
      } else {
        // Переход через день
        const minutesDay1 = Math.max(0, shiftEnd - clampedStart);
        const minutesDay2 = Math.max(0, clampedEnd - shiftStart);
        minutes = minutesDay1 + minutesDay2;
      }
      return minutesToHours(minutes);
    }

    const segment1 = calculateSegment(startTime, endTime);
    const segment2 = (starttimes && endtimes) ? calculateSegment(starttimes, endtimes) : 0;

    return (segment1 + segment2).toFixed(2);
  }

  const sortRows = (rows) => {
    const { key, order } = sortConfig;
    if (!key || order === SORT_ORDER.NONE) return rows;
    const col = columns.find(c => c.key === key);
    const dir = order === SORT_ORDER.ASC ? 1 : -1;
    const getValue = (row) => {
      const v = row[key];
      if (v === null || v === undefined || v === '') return null;
      if (col && (col.type === 'date' || col.type === 'datetime')) {
        const t = new Date(v).getTime();
        return isNaN(t) ? null : t;
      }
      const num = parseFloat(v);
      return !isNaN(num) && isFinite(v) ? num : String(v).toLowerCase();
    };
    return [...rows].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
  };

  const fetchData = async () => {
    const fetchId = ++fetchIdRef.current;
    try {
      let combinedData = [];
      if (endpoint.includes('sbproduction')) {
        const plannedResponse = await api.get(`/api/get_planned_production_plan`, {
          params: { page, limit, search: searchTerm || '', ...filterParams, sortKey: sortConfig.key, sortOrder: sortConfig.order === SORT_ORDER.NONE ? 'ASC' : sortConfig.order }
        });
        const searchLower = (searchTerm || '').toLowerCase();
        const plannedData = (plannedResponse.data || []).filter(item =>
          !searchLower || `${item.sbsku} ${item.packName}`.toLowerCase().includes(searchLower)
        ).map(item => ({
          ...item,
          status: 'Planned',
          sku: item.sbsku,
          prodDate: item.date,
          qty: item.qty,
          recID: '',
          planned_production_id: item.id
        }));

        const productionResponse = await api.get(endpoint, {
          params: { page, limit, search: searchTerm || '', ...filterParams, sortKey: sortConfig.key, sortOrder: sortConfig.order === SORT_ORDER.NONE ? 'ASC' : sortConfig.order }
        });
        const productionData = (productionResponse.data.items || productionResponse.data.data || productionResponse.data || []).map(item => ({
          ...item,
          planned_production_id: item.planned_production_id,
          hoursTook: calculateHoursTook(item)
        }));

        combinedData = [...plannedData, ...productionData];
      } else {
        const response = await api.get(endpoint, {
          params: { page, limit, search: searchTerm || '', ...filterParams, sortKey: sortConfig.key, sortOrder: sortConfig.order === SORT_ORDER.NONE ? 'ASC' : sortConfig.order }
        });
        combinedData = response.data.items || response.data.data || response.data || [];
      }

      if (fetchId !== fetchIdRef.current) return; // stale response from a superseded request

      const dataWithFormattedDates = combinedData.map(item => {
        for (const key in item) {
          if (item.hasOwnProperty(key)) {
            const column = columns.find(col => col.key === key);
            if (column) {
              if (column.type === 'date') item[key] = formatDateTime(item[key], '', 'date');
              else if (column.type === 'datetime') item[key] = formatDateTime(item[key], '', 'datetime');
              else if (column.type === 'time') item[key] = formatDateTime('', item[key], 'time');
            }
          }
        }
        return item;
      });

      // Planned + production приходят двумя списками — сортируем объединённый список на клиенте
      setDataState(endpoint.includes('sbproduction') ? sortRows(dataWithFormattedDates) : dataWithFormattedDates);
      setTotalItems(combinedData.length);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, limit, searchTerm, filterParams, sortConfig, updateKey]);

  const handleSort = (key) => {
    let newOrder = sortConfig.key === key ?
      (sortConfig.order === SORT_ORDER.ASC ? SORT_ORDER.DESC : sortConfig.order === SORT_ORDER.DESC ? SORT_ORDER.NONE : SORT_ORDER.ASC) :
      SORT_ORDER.ASC;
    setSortConfig({ key, order: newOrder });
  };

  const handleDelete = async (rowData) => {
    const table = endpoint.split('/').pop();
    let url;

    if (rowData.status === 'Planned') {
      if (!rowData.planned_production_id) {
        console.error('No planned_production_id provided for deletion');
        return;
      }
      url = `/api/sbplanned_production/${rowData.planned_production_id}`;
    } else {
      const keyFields = keyFieldsConfig[table];
      if (Array.isArray(keyFields)) {
        const id = keyFields.map(key => `${key}=${rowData[key]}`).join('&');
        url = `${endpoint}?${id}`;
      } else {
        const id = rowData[keyFields];
        if (!id) return;
        url = `${endpoint}/${id}`;
      }
    }

    try {
      await api.delete(url);
      fetchData();
    } catch (error) {
      console.error('Error deleting data:', error);
    }
  };

  const handleEdit = (rowData) => {
    const table = endpoint.split('/').pop();
    const keyFields = keyFieldsConfig[table];
    const id = Array.isArray(keyFields) ? keyFields.map(key => rowData[key]).join('-') : rowData[keyFields];
    onEdit(rowData, id);
  };

  const handleAddToProduction = (rowData) => {
    onAddFromPlanned(rowData);
  };

  const handleStatusChange = async (newStatus, rowIndex) => {
    const updatedData = [...data];
    const row = updatedData[rowIndex];
    updatedData[rowIndex].status = newStatus;
    setDataState(updatedData);
    setActiveStatusRow(null);

    try {
      await api.post(`/api/update_production_status`, {
        id: row.id,
        status: newStatus
      });
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const toggleStatusDropdown = (rowIndex) => {
    setActiveStatusRow(rowIndex === activeStatusRow ? null : rowIndex);
  };

  const totalPages = Math.ceil(totalItems / limit);

  return (
    <div>
      <style>
        {`
          .small-time {
            font-size: 0.75em;
            color: #6B7280;
            font-weight: bold;
            letter-spacing: -0.02em;
            font-family: Arial, Helvetica, sans-serif;
            white-space: nowrap;
            overflow-wrap: break-word;
          }
        `}
      </style>
      <h2>{title}</h2>
      <div id="tablecontent">
        <table className="table">
          <thead>
            <tr>
              {showStatus && <th className="table__heading"> </th>}
              {columns.map((col, index) => (
                <th key={index} className="table__heading" onClick={() => handleSort(col.key)}>
                  {sortConfig.key === col.key && (
                    <svg className="icon icon__sort" width="16" height="16">
                      <use xlinkHref="#sort" />
                    </svg>
                  )}
                  {col.label}
                </th>
              ))}
              <th className="table__heading">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {showStatus && (
                  <td className="table__status">
                    <div
                      className="status-indicator"
                      style={{ backgroundColor: 'transparent', cursor: row.status !== 'Planned' ? 'pointer' : 'default' }}
                      title={row.status}
                      onClick={() => row.status !== 'Planned' && toggleStatusDropdown(rowIndex)}
                    >
                      {row.status === 'Planned' && <i className="fa-solid fa-clock" style={{ color: '#f5b400', fontSize: '22px' }}></i>}
                      {(row.status === 'In production' || row.status === 'In Progress') && <i className="fa-solid fa-circle-half-stroke" style={{ color: '#f5b400', fontSize: '22px' }}></i>}
                      {row.status === 'Complete' && <i className="fa-regular fa-circle-check" style={{ color: '#5B965D', fontSize: '22px' }}></i>}
                    </div>
                    {activeStatusRow === rowIndex && (
                      <div className="status-dropdown">
                        {STATUS_OPTIONS.map((statusOption) => (
                          <div
                            key={statusOption}
                            className="status-option"
                            onClick={() => handleStatusChange(statusOption, rowIndex)}
                          >
                            {statusOption}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                )}
                {columns.map((col, colIndex) => {
                  let cellContent;
                  if (col.type === 'image') {
                    const sku = row.SKU || row.sku;
                    cellContent = sku ? (
                      <ImageWithPreview
                        src={`${API_URL}/api/product-images/${String(sku).trim().replace(/[^\w.-]/g, '_')}.jpg`}
                        alt=""
                        style={{ width: 36, height: 36, borderRadius: 4 }}
                      />
                    ) : null;
                  } else if (col.key === 'plannedlb') {
                    cellContent = (row.status === 'Planned' ? '' : (row[col.key] ? parseFloat(row[col.key]).toFixed(2) : ''));
                  } else {
                    cellContent = row[col.key];
                    if (endpoint.includes('sbproduction')) {
                      if (col.key === 'prodDate') {
                        const startTimeFormatted = formatTime(row.startTime);
                        const endTimeFormatted = formatTime(row.endTime);
                        const timeRange = endTimeFormatted ? `${startTimeFormatted} - ${endTimeFormatted}` : startTimeFormatted;
                        cellContent = (
                          <div>
                            <div>{row[col.key]}</div>
                            {timeRange && <div className="small-time">{timeRange}</div>}
                          </div>
                        );
                      } else if (col.key === 'finishdate') {
                        if (row.finishdate && row.finishdate !== row.prodDate) {
                          const startTimesFormatted = formatTime(row.starttimes);
                          const endTimesFormatted = formatTime(row.endtimes);
                          const timeRange = endTimesFormatted ? `${startTimesFormatted} - ${endTimesFormatted}` : startTimesFormatted;
                          cellContent = (
                            <div>
                              <div>{row[col.key]}</div>
                              {timeRange && <div className="small-time">{timeRange}</div>}
                            </div>
                          );
                        }
                      } else if (col.key === 'packName' && row.status === 'Planned' && row.use_substitute) {
                        // План поставлен в расчёте на семена-замены (Daily Plan, Use substitute seeds)
                        cellContent = (
                          <>
                            {row.packName}
                            <i className="fa-solid fa-right-left"
                              title={`Planned with substitute seeds${row.substituteNames ? `: ${row.substituteNames}` : ''}`}
                              style={{ marginLeft: 6, color: '#d97706' }}></i>
                          </>
                        );
                      }
                    }
                  }
                  return (
                    <td className="table__text" key={colIndex}>
                      {cellContent}
                    </td>
                  );
                })}
                <td className="table__actions">
                  {row.status === 'Planned' ? (
                    <>
                      <svg className="icon icon__add-to-production" width="16" height="16" viewBox="0 0 512 512" onClick={() => handleAddToProduction(row)}>
                        <use xlinkHref="#file" />
                      </svg>
                      {canDelete ? (
                        <svg className="icon icon__delete" width="16" height="16" viewBox="0 0 512 512" onClick={() => handleDelete(row)}>
                          <use xlinkHref="#delete" />
                        </svg>
                      ) : (
                        <svg className="icon icon__delete" width="16" height="16" viewBox="0 0 512 512" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                          <use xlinkHref="#delete" />
                        </svg>
                      )}
                    </>
                  ) : (
                    <>
                      {onEdit && (
                        <svg className="icon icon__edit" width="16" height="16" viewBox="0 0 512 512" onClick={() => handleEdit(row)}>
                          <use xlinkHref="#edit" />
                        </svg>
                      )}
                      {canDelete ? (
                        <svg className="icon icon__delete" width="16" height="16" viewBox="0 0 512 512" onClick={() => handleDelete(row)}>
                          <use xlinkHref="#delete" />
                        </svg>
                      ) : (
                        <svg className="icon icon__delete" width="16" height="16" viewBox="0 0 512 512" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                          <use xlinkHref="#delete" />
                        </svg>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} totalPages={totalPages} limit={limit} setPage={setPage} setLimit={setLimit} />
    </div>
  );
};

export default DataTable;